/*
 *   File : data-table.js
 *   Author : https://evoqins.com
 *   Description : data table component
 *   Integrations : NA
 *   Version : 1.0.0
 */

import React, { memo, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { ScrollInfo } from "../Other";
import Icon from "../icons";

// Module-level so the table's selection effect doesn't re-run on every render
const _selectEveryRow = () => true;

const DataTableComponent = (props) => {
  const {
    info = false,
    columns,
    data,
    pagination,
    paginationServer,
    paginationTotalRows,
    paginationPerPage,
    currentPage,
    onChangePage,
    onChangeRowsPerPage,
    selectableRows,
    // true while "Select All" is active: keeps every page's rows selected
    allSelected = false,
    onSelectedRowsChange,
    onRowClicked,
    className,
    sortServer,
    onSort,
    defaultSortFieldId,
    defaultSortAsc,
  } = props;

  const wrapperRef = useRef(null);
  // Set when the user ticks a checkbox. The table also reports selection on its own
  // when it mounts (StrictMode replays that with an empty selection), and those echoes
  // would switch Select All off, so only user-driven changes are passed up.
  const userSelectRef = useRef(false);

  const _handleSelectedRowsChange = (state) => {
    if (!userSelectRef.current) return;
    userSelectRef.current = false;
    onSelectedRowsChange && onSelectedRowsChange(state);
  };

  // While Select All is on, every remount (new page) reports the new page's rows as selected
  useEffect(() => {
    if (!allSelected || !onSelectedRowsChange) return;
    onSelectedRowsChange({
      allSelected: true,
      selectedCount: data.length,
      selectedRows: data.slice(0),
    });
  }, [allSelected, data]);

  // Scrollbar is hidden, so let mouse users drag the table horizontally
  const _handleMouseDown = (e) => {
    if (e.button !== 0 || e.target.closest("input, button, a, select")) return;
    const scroller = wrapperRef.current
      ?.querySelector(".rdt_Table")
      ?.parentElement?.parentElement;
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

    const startX = e.pageX;
    const startScroll = scroller.scrollLeft;
    let dragged = false;

    const onMove = (ev) => {
      const dx = ev.pageX - startX;
      if (!dragged && Math.abs(dx) < 5) return;
      if (!dragged) {
        dragged = true;
        scroller.classList.add("e-table-dragging");
      }
      scroller.scrollLeft = startScroll - dx;
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      scroller.classList.remove("e-table-dragging");
      if (dragged) {
        // swallow the click that follows a drag so rows/links don't fire
        const stopClick = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        window.addEventListener("click", stopClick, { capture: true, once: true });
        setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={wrapperRef}
      className="d-flex flex-column rg-12"
      onMouseDown={_handleMouseDown}
      onClickCapture={(e) => {
        if (e.target.type === "checkbox") userSelectRef.current = true;
      }}
    >
      {info && (
        <div className="d-flex justify-content-end pe-30">
          <ScrollInfo />
        </div>
      )}
      <DataTable
        columns={columns}
        data={data}
        // ✅ DISABLE built-in pagination completely
        pagination={false}
        selectableRows={selectableRows}
        // The table remounts per page, so re-select the new page's rows while Select All is on
        selectableRowSelected={allSelected ? _selectEveryRow : undefined}
        onSelectedRowsChange={_handleSelectedRowsChange}
        sortIcon={<Icon icon="sort-icon" size="14px" className="ms-2" />}
        showIcon
        sortServer={sortServer}
        onSort={onSort}
        defaultSortFieldId={defaultSortFieldId}
        defaultSortAsc={defaultSortAsc}
        onRowClicked={onRowClicked}
        className={className}
      />

      {/* ✅ Render custom pagination OUTSIDE DataTable */}
      {pagination && (
        <Pagination
          currentPage={currentPage}
          rowsPerPage={paginationPerPage}
          rowCount={paginationTotalRows}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      )}
    </div>
  );
};

DataTableComponent.defaultProps = {
  data: [],
  pagination: true,
  paginationServer: true,
  paginationPerPage: 10,
  onRowClicked: () => {},
};

export default memo(DataTableComponent);

const Pagination = ({
  currentPage,
  rowsPerPage,
  rowCount,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const safeCurrentPage = currentPage || 1;
  const safeRowsPerPage = rowsPerPage || 10;
  const safeRowCount = rowCount || 0;

  const totalPages = Math.ceil(safeRowCount / safeRowsPerPage);

  const goTo = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    if (page !== safeCurrentPage && onChangePage) {
      onChangePage(page);
    }
  };

  const generatePages = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safeCurrentPage > 3) pages.push("…");

      const start = Math.max(2, safeCurrentPage - 1);
      const end = Math.min(totalPages - 1, safeCurrentPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (safeCurrentPage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }

    return pages;
  };

  if (totalPages === 0) {
    return null;
  }

  return (
    <div className="e-pagination-container d-flex align-items-center justify-content-end px-3 pb-3 pt-3 gap-2 mt-0">
      {/* Left side: Rows per page selector */}
      {/* <div className="d-flex align-items-center gap-2">
        <span className="text-muted fs-14">Rows per page:</span>
        <select
          className="form-select form-select-sm"
          style={{ width: "80px" }}
          value={safeRowsPerPage}
          onChange={(e) => {
            const newPerPage = parseInt(e.target.value, 10);
            if (onChangeRowsPerPage) {
              onChangeRowsPerPage(newPerPage);
            }
          }}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-muted fs-14">
          {Math.min((safeCurrentPage - 1) * safeRowsPerPage + 1, safeRowCount)}-
          {Math.min(safeCurrentPage * safeRowsPerPage, safeRowCount)} of {safeRowCount}
        </span>
      </div> */}

      {/* Right side: Page navigation */}
      <div className="d-flex align-items-center gap-2">
        <button
          className="e-page-btn"
          onClick={() => goTo(1)}
          disabled={safeCurrentPage === 1}
          title="First page"
        >
          ««
        </button>

        <button
          className="e-page-btn"
          onClick={() => goTo(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          title="Previous page"
        >
          ‹
        </button>

        {generatePages().map((page, i) =>
          page === "…" ? (
            <span key={i} className="e-dots">
              …
            </span>
          ) : (
            <button
              key={i}
              className={`e-page-btn ${safeCurrentPage === page ? "active" : ""}`}
              onClick={() => goTo(page)}
            >
              {page}
            </button>
          ),
        )}

        <button
          className="e-page-btn"
          onClick={() => goTo(safeCurrentPage + 1)}
          disabled={safeCurrentPage === totalPages}
          title="Next page"
        >
          ›
        </button>

        <button
          className="e-page-btn"
          onClick={() => goTo(totalPages)}
          disabled={safeCurrentPage === totalPages}
          title="Last page"
        >
          »»
        </button>
      </div>
    </div>
  );
};
